import { jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { useCellData } from "./useCellData.js";
import { INDEX_COLUMN_INDEX, INDEX_ROW_INDEX } from "../Table/constants.js";
import { useColumns } from "../../context/useColumnsContext.js";
import { cellIsSelected, cellIsFocused } from "../../hooks/useSelectCells.js";
import { EColumnType } from "../../types/Column.js";
import Renderer$3 from "./Boolean/Renderer.js";
import Editor from "./Default/Editor.js";
import Renderer from "./Default/Renderer.js";
import Editor$4 from "./Enum/Editor.js";
import Renderer$5 from "./Enum/Renderer.js";
import Renderer$1 from "./EnumList/Renderer.js";
import Editor$1 from "./EnumList/Editor.js";
import ColumnHeader from "./Index/ColumnHeader.js";
import RowHeader from "./Index/RowHeader.js";
import LoadingRenderer from "./LoadingRenderer.js";
import { Editor as Editor$2 } from "./MultiValue/Editor.js";
import Renderer$2 from "./MultiValue/Renderer.js";
import Editor$3 from "./Number/Editor.js";
import Renderer$4 from "./Number/Renderer.js";
const getColumnStyle = (columnIndex, isFirstRow, data) => {
  if (columnIndex === INDEX_COLUMN_INDEX) {
    return { left: 0, width: data.theme.indexColumn.width };
  }
  const headerStyles = isFirstRow ? data.columnStyles[columnIndex].header : {};
  return {
    ...data.columnStyles[columnIndex],
    ...headerStyles
  };
};
const getClassName = (columnIndex, data) => {
  const columnClassName = data.columnClassNames[columnIndex] || "";
  const cellClassName = data.cellClassName || "";
  return [columnClassName, cellClassName].filter(Boolean).join(" ");
};
const getCellStatus = (cell, column) => {
  var _a, _b, _c, _d;
  let cellStatus = {};
  if ((cell == null ? void 0 : cell.disabled) || (column == null ? void 0 : column.disabled)) {
    cellStatus["disabled"] = true;
  }
  if ((_b = (_a = cell == null ? void 0 : cell.status) == null ? void 0 : _a.error) == null ? void 0 : _b.length) {
    cellStatus["error"] = true;
  } else if ((_d = (_c = cell == null ? void 0 : cell.status) == null ? void 0 : _c.warning) == null ? void 0 : _d.length) {
    cellStatus["warning"] = true;
  }
  return cellStatus;
};
const getCellComponent = (column) => {
  if (column == null ? void 0 : column.renderer)
    return column.renderer;
  switch (column == null ? void 0 : column.type) {
    case EColumnType.enum:
      return Renderer$5;
    case EColumnType.number:
      return Renderer$4;
    case EColumnType.boolean:
      return Renderer$3;
    case EColumnType.stringList:
      return Renderer$2;
    case EColumnType.enumList:
      return Renderer$1;
    default:
      return Renderer;
  }
};
const getCellEditorComponent = (column, cell) => {
  if (cell == null ? void 0 : cell.editor)
    return cell.editor;
  if (column == null ? void 0 : column.editor)
    return column.editor;
  switch (column == null ? void 0 : column.type) {
    case EColumnType.enum:
      return Editor$4;
    case EColumnType.number:
      return Editor$3;
    case EColumnType.stringList:
      return Editor$2;
    case EColumnType.enumList:
      return Editor$1;
    default:
      return Editor;
  }
};
const Cell = (props) => {
  var _a;
  const { getAbsoluteColumnIndex } = useColumns();
  const { data, style, pinned, columnIndex, rowIndex, header } = props;
  const {
    columns,
    columnDiff,
    columnSelectionEnabled,
    columnContextMenuSlot,
    pinnedColumns,
    onSort,
    sortOverride,
    onFindAndReplace,
    onSearchValue,
    tableId,
    useFilterByValue,
    isLoading,
    pinningEnabled,
    showColumnMenus,
    decorateCell,
    hasColumnMetaRow,
    cellContextMenuSlot,
    headerInjections
  } = data;
  const hasPinnedColumns = (pinnedColumns == null ? void 0 : pinnedColumns.length) > 0;
  const totalColumns = columns.length;
  const totalRows = data.totalRowCount + data.additionalRows;
  const isIndexColumn = columnIndex === INDEX_COLUMN_INDEX;
  const absoluteRowIndex = !isIndexColumn && hasColumnMetaRow ? rowIndex - 2 : rowIndex - 1;
  const absoluteColumnIndex = getAbsoluteColumnIndex(columnIndex);
  const column = columns[absoluteColumnIndex];
  const { row, cell, columnConfig, onUpdateCell } = useCellData({
    row: absoluteRowIndex,
    column: absoluteColumnIndex,
    columns
  });
  const isFirstColumn = columnIndex === 0;
  const isFirstColumnAfterPinned = hasPinnedColumns && columnIndex === (pinnedColumns == null ? void 0 : pinnedColumns.length);
  const isFirstRow = rowIndex === INDEX_ROW_INDEX;
  const isFirstNonHeaderRow = rowIndex === INDEX_ROW_INDEX + 1;
  const isLastRow = rowIndex === totalRows;
  const isLastColumn = columnIndex === totalColumns - 1;
  const isGhostRow = !row;
  const isActiveGhostRow = !!(row && !row.id);
  const isHidden = hasPinnedColumns && columnIndex < (pinnedColumns == null ? void 0 : pinnedColumns.length) && !pinned;
  const cellCoordinates = [rowIndex, columnIndex];
  const cellProps = useMemo(
    () => ({
      decorateCell,
      pinned,
      tableId,
      style: {
        ...style,
        ...getColumnStyle(columnIndex, isFirstRow, data)
      },
      isLastColumn,
      isFirstColumn: isIndexColumn || isFirstColumn || isFirstColumnAfterPinned,
      isSelected: cellIsSelected(cellCoordinates, totalColumns, tableId),
      onMouseDown: (cellRef, button, shiftKey) => data.onCellMouseDown(cellRef, cellCoordinates, button === 2, shiftKey),
      onMouseEnter: (cellRef) => data.onCellMouseEnter(cellRef, cellCoordinates),
      onMouseUp: (cellRef) => data.onCellMouseUp(cellRef, cellCoordinates),
      onClick: () => {
        var _a2;
        return (_a2 = data.onCellClick) == null ? void 0 : _a2.call(data, { ...row ?? {}, ...cell });
      },
      absoluteColumnIndex,
      theme: data.theme
    }),
    [
      pinnedColumns,
      data.columnStyles,
      tableId,
      decorateCell,
      (_a = cell.metadata) == null ? void 0 : _a.acceptedValue
    ]
  );
  if (isHidden) {
    return null;
  }
  const canInsert = !data.readOnly && !isFirstRow && !isGhostRow && data.canInsertRows;
  if (isIndexColumn) {
    return /* @__PURE__ */ jsx(
      RowHeader,
      {
        ...cellProps,
        rowData: row,
        onSelectedRows: data.onSelectedRows,
        rowIndex,
        isLastRow,
        isFirstRow,
        isIndexColumn,
        isGhostRow,
        totalRowCount: data.totalRowCount,
        rowSelectionType: data.rowSelectionType,
        clearCellSelection: data.clearCellSelection,
        decorateCell,
        canInsert
      }
    );
  }
  if (isFirstRow) {
    return /* @__PURE__ */ jsx(
      ColumnHeader,
      {
        ...cellProps,
        header,
        column: columnConfig,
        readOnly: data.readOnly,
        sortOverride,
        onSearchValue,
        onSort,
        onFindAndReplace,
        useFilterByValue,
        columnChangeType: columnDiff[column.value],
        columnSelectionEnabled,
        pinningEnabled,
        showColumnMenu: showColumnMenus,
        columnContextMenuSlot,
        headerInjections
      }
    );
  }
  const CellComponent = isLoading ? LoadingRenderer : getCellComponent(columnConfig);
  const EditorComponent = getCellEditorComponent(columnConfig, cell);
  return CellComponent({
    ...cellProps,
    rowData: row,
    row: absoluteRowIndex,
    column: absoluteColumnIndex,
    columnConfig,
    className: getClassName(absoluteColumnIndex, data),
    cell: data.cell ?? cell,
    status: getCellStatus(cell, columnConfig),
    active: cell == null ? void 0 : cell.active,
    editor: EditorComponent,
    mutated: cell == null ? void 0 : cell.mutated,
    required: columnConfig == null ? void 0 : columnConfig.required,
    readOnly: data.readOnly,
    exitSelectionState: data.exitSelectionState,
    onUpdateCell: data.onUpdateCell ?? onUpdateCell,
    onAddNewRow: data.onAddNewRow,
    totalRows,
    totalColumns,
    isDiffView: data.isDiffView,
    isLastRow,
    isLastColumn,
    isGhostRow,
    isActiveGhostRow,
    isFirstNonHeaderRow,
    isFocused: cellIsFocused(cellCoordinates, tableId),
    decorateCell,
    scrollToNextRow: isLastColumn && !isLastRow ? () => {
      data.scrollToItem(rowIndex + 1, 0);
    } : void 0,
    scrollToPreviousRow: isFirstColumn && !isFirstNonHeaderRow ? () => {
      data.scrollToItem(rowIndex - 1, totalColumns - 1);
    } : void 0,
    onSelectedRows: data.onSelectedRows,
    onCellFocus: (cellRef, contextMenuOpen) => data.onCellFocus(cellRef, cellCoordinates, contextMenuOpen),
    onCellTab: (cellRef, shiftKey) => data.onCellTab(cellRef, cellCoordinates, void 0, shiftKey),
    onCellArrowRight: (cellRef) => data.onCellArrowRight(cellRef, cellCoordinates),
    onCellArrowLeft: (cellRef) => data.onCellArrowLeft(cellRef, cellCoordinates),
    onCellArrowUp: (cellRef) => data.onCellArrowUp(cellRef, cellCoordinates),
    onCellArrowDown: (cellRef) => data.onCellArrowDown(cellRef, cellCoordinates),
    ["data-column-index"]: absoluteColumnIndex,
    cellContextMenuSlot,
    isNewRowCell: data.isNewRowCell
  });
};
export {
  Cell as default,
  getCellComponent,
  getCellEditorComponent
};
